import React, { useState, useEffect, useRef } from 'react';
import { Container, Row } from 'reactstrap';
import { Sidebar } from './Sidebar';
import { MenuItem } from './MenuItem';
import { ItemDetailPopup } from './ItemDetailPopup';
import FoodMenuService from '../../../Services/QRCodeServices/CustomerService/FoodMenuService';
import { useParams } from 'react-router-dom';
import { Navbar } from "./navbar/Navbar";
import { Footer } from "./footer/Footer";

export const Theme9Menu = ({ orgInfo, category }) => {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const { orgcode } = useParams();
  const categoryRefs = useRef({});
  const observerRef = useRef(null);

  useEffect(() => {
    const fetchMenuData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await FoodMenuService.getFoodMenu(orgcode);
        if (response && Array.isArray(response.allMenu)) {
          setMenuData(response.allMenu);

          if (category) {
            const decodedCategory = decodeURIComponent(category.toLowerCase());
            setActiveCategory(decodedCategory);

            setTimeout(() => {
              const categoryRef = categoryRefs.current[decodedCategory];
              if (categoryRef) {
                const navbarHeight = document.querySelector('.navbar')?.offsetHeight || 0;
                const yOffset = -navbarHeight - 20;
                const y = categoryRef.getBoundingClientRect().top + window.pageYOffset + yOffset;

                window.scrollTo({
                  top: y,
                  behavior: 'smooth'
                });
              }
            }, 100);
          }
        } else {
          throw new Error('Invalid menu data format received.');
        }
      } catch (err) {
        setError('Failed to load menu data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchMenuData();
  }, [orgcode, category]);

  useEffect(() => {
    // Set up the Intersection Observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const categoryName = entry.target.id;
            setActiveCategory(categoryName);
          }
        });
      },
      {
        rootMargin: '-50% 0px -50% 0px', // Adjust this value to control when the active category changes
        threshold: 0
      }
    );

    // Observe each category section
    Object.values(categoryRefs.current).forEach(ref => {
      if (ref) {
        observerRef.current.observe(ref);
      }
    });

    // Cleanup observer on component unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [menuData]);

  const handleCategoryClick = (categoryName) => {
    const lowercaseCategory = categoryName.toLowerCase();
    setActiveCategory(lowercaseCategory);
    if (categoryRefs.current[lowercaseCategory]) {
      const navbarHeight = document.querySelector('.navbar')?.offsetHeight || 0;
      const yOffset = -navbarHeight - 20;
      const y = categoryRefs.current[lowercaseCategory].getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  const handleAddToCart = (item, quantity) => {
    console.log(`Added ${quantity} of ${item.title} to cart`);
    setSelectedItem(null);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-4" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="menu-layout container w-100">
      <Navbar orgInfo={orgInfo} />
      {/* Mobile Sidebar */}
      <div className="d-md-none">
        <Sidebar
          categories={menuData}
          activeCategory={activeCategory}
          onCategoryClick={handleCategoryClick}
          isMobile={true}
        />
      </div>

      {/* Desktop Sidebar */}
      <div className="desktop-sidebar-container d-none d-md-block">
        <Sidebar
          categories={menuData}
          activeCategory={activeCategory}
          onCategoryClick={handleCategoryClick}
          isMobile={false}
        />
      </div>

      {/* Main Content */}
      <div className="main-content-wrapper">
        <div className="menu-content cursor-pointer">
          {menuData.map((menu) => (
            <div
              key={menu.id}
              ref={(el) => (categoryRefs.current[menu.name.toLowerCase()] = el)}
              id={menu.name.toLowerCase()}
              className="mb-5 mt-5"
              style={{ paddingTop: '25px',}} 
            >
              <h3 className="mb-4 text-black text-capitalize pt-3">{menu.name}</h3>
              <Row>
                {menu.menuItem.map((item) => (
                  <MenuItem key={item.id} item={item} onClick={handleItemClick} orgInfo={orgInfo} />
                ))}
              </Row>
            </div>
          ))}
        </div>
      </div>

      {/* Item Detail Popup */}
      <ItemDetailPopup
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onAddToCart={handleAddToCart}
        orgInfo={orgInfo}
      />

      <Footer orgInfo={orgInfo} />
    </div>
  );
};